import ShortlistedSummaryPanel from 'Components/campaigns/applications/ShortlistedSummaryPanel';
import { defaultShortlistedFilters } from 'Components/campaigns/applications/ApplicationFilters';
import { PRIVILEGE } from 'constants/privilege';
import withAccessControl from 'hoc/withAccessControl';
import CampaignApplicationsList from 'pages/Campaigns/Applications/CampaignApplicationsList';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { approveShortlistedApplication, fetchShortlistedSummary } from 'services/api/campaign/application';
import { invalidateCampaignApplicationLists } from 'slices/campaigns/applications/reducer';
import { fetchCampaignApplications } from 'slices/campaigns/applications/thunk';
import { invalidateCampaignCollaborationLists } from 'slices/campaigns/collaborations/reducer';
import { markCampaignDataExpired } from 'slices/campaigns/reducer';
import { AppDispatch, RootState } from 'store';
import { ShortlistedSummary } from 'types/campaign/applications';
import { getErrorMessage } from 'utils/helpers';
import { useGetCampaignCurrency } from 'slices/campaigns/selector';

const SUMMARY_FETCH_DEBOUNCE_MS = 200;
const LAUNCH_REDIRECT_DELAY_MS = 10_000;

const LAUNCH_SUCCESS_TOAST =
    'We are initiating the collabs for the shortlisted creators, it may take a few seconds for all the collabs to appear.';

const ShortlistedApps = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const { campaignId } = useParams();
    const campaign = useSelector((state: RootState) => state.campaigns.items[campaignId!]?.data?.campaign);
    const currency = useGetCampaignCurrency(campaignId!);
    const [shortlistedSummary, setShortlistedSummary] = useState<ShortlistedSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const summaryRequestIdRef = useRef(0);
    const launchRedirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [launchingCampaign, setLaunchingCampaign] = useState(false);
    const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);

    const filtersShortlisted = useSelector(
        (state: RootState) => state.campaignApplications.filtersShortlisted
    );
    const shortlistedSummaryEpoch = useSelector(
        (state: RootState) =>
            state.campaignApplications.shortlistedSummaryEpoch[campaignId!] ?? 0
    );

    const shortlistedListFilters = useMemo(
        () => ({ ...filtersShortlisted, bookmarked: 'y' as const, page: 1 }),
        [filtersShortlisted]
    );

    const loadShortlistedSummary = useCallback(async () => {
        if (!campaignId || !selectedAgencyId) {
            setSummaryLoading(false);
            return;
        }

        const requestId = ++summaryRequestIdRef.current;
        setSummaryLoading(true);

        try {
            const summary = await fetchShortlistedSummary(selectedAgencyId, campaignId);
            if (requestId !== summaryRequestIdRef.current) {
                return;
            }
            setShortlistedSummary(summary);
        } catch {
            if (requestId !== summaryRequestIdRef.current) {
                return;
            }
            setShortlistedSummary(null);
        } finally {
            if (requestId === summaryRequestIdRef.current) {
                setSummaryLoading(false);
            }
        }
    }, [campaignId, selectedAgencyId]);

    useEffect(() => {
        if (!campaignId || !selectedAgencyId) {
            setSummaryLoading(false);
            return;
        }

        const timer = window.setTimeout(() => {
            loadShortlistedSummary();
        }, SUMMARY_FETCH_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
            summaryRequestIdRef.current += 1;
        };
    }, [campaignId, selectedAgencyId, summaryRefreshKey, shortlistedSummaryEpoch, loadShortlistedSummary]);

    useEffect(
        () => () => {
            if (launchRedirectTimerRef.current) {
                clearTimeout(launchRedirectTimerRef.current);
            }
        },
        []
    );

    const launchCampaign = useCallback(async () => {
        if (!campaignId || !selectedAgencyId) {
            toast.error('Unable to launch campaign. Please refresh and try again.');
            throw new Error('Missing campaign context');
        }

        if (launchRedirectTimerRef.current) {
            clearTimeout(launchRedirectTimerRef.current);
            launchRedirectTimerRef.current = null;
        }

        setLaunchingCampaign(true);

        try {
            const result = await approveShortlistedApplication(selectedAgencyId, campaignId);

            if (!result?.status) {
                throw new Error(getErrorMessage(result, 'Failed to launch campaign'));
            }

            dispatch(
                invalidateCampaignApplicationLists({ campaignId, clearLists: true })
            );
            dispatch(invalidateCampaignCollaborationLists({ campaignId }));
            dispatch(markCampaignDataExpired({ campaignId }));

            setShortlistedSummary(null);
            setSummaryRefreshKey((key) => key + 1);

            void dispatch(
                fetchCampaignApplications({
                    campaignId,
                    filters: shortlistedListFilters,
                    force: true,
                })
            );

            toast.success(LAUNCH_SUCCESS_TOAST, { autoClose: LAUNCH_REDIRECT_DELAY_MS });

            launchRedirectTimerRef.current = setTimeout(() => {
                launchRedirectTimerRef.current = null;
                setLaunchingCampaign(false);
                window.location.href = `/campaign/${campaignId}/collaborations`;
            }, LAUNCH_REDIRECT_DELAY_MS);
        } catch (error) {
            setLaunchingCampaign(false);
            toast.error(getErrorMessage(error, 'Failed to launch campaign'), { autoClose: 10000});
        }
    }, [campaignId, dispatch, selectedAgencyId, shortlistedListFilters]);

    return (
        <CampaignApplicationsList
            bookmarked="y"
            tabDefaultFilters={defaultShortlistedFilters}
            resetFilters={defaultShortlistedFilters}
            documentTitle="Shortlisted Applications - Campaign"
            emptyStateMessage="Currently there are no shortlisted applications, try adjusting filters!"
            viewBestCreative={campaign?.attach_social_post}
            sidebar={
                <ShortlistedSummaryPanel
                    currency={currency!}
                    summary={shortlistedSummary}
                    loading={summaryLoading}
                    hasTransactions={Boolean(campaign?.has_transactions)}
                    onLaunchCampaign={launchCampaign}
                    launchingCampaign={launchingCampaign}
                />
            }
        />
    );
};

export default withAccessControl(ShortlistedApps, PRIVILEGE.CAMPAIGN.APPLICATION.INDEX);
